import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/authStore';
import { useNotifications } from '../notifications/notificationsStore';
import brandLogo from '../assets/img/logo.svg';
import { resolveUrl } from '../utils/urls';

function getInitials(me) {
  const source = (me?.name || me?.email || '').trim();
  if (!source) return 'Г';
  if (source.includes('@')) return source.slice(0, 2).toUpperCase();
  const parts = source.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || source.slice(0, 2).toUpperCase();
}

function getRoleLabel(role) {
  if (role === 'ADMIN') return 'Админ';
  if (role === 'PSYCHOLOGIST') return 'Психолог';
  return 'Клиент';
}

export default function Header() {
  const { booting, isAuthenticated, me, role, logout } = useAuth();
  const { unreadNotificationsCount = 0, messageUnreadTotal = 0 } = useNotifications();

  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);

  const unreadTotal = Math.max(0, Number(unreadNotificationsCount || 0)) + Math.max(0, Number(messageUnreadTotal || 0));
  const initials = useMemo(() => getInitials(me), [me]);
  const roleLabel = getRoleLabel(role);
  const avatarRaw = String(
    me?.avatarUrl || me?.avatar_url || me?.avatar || me?.photoUrl || me?.photo_url || me?.image || ''
  ).trim();
  const avatarUrl = avatarRaw ? resolveUrl(avatarRaw) : '';

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const onLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="site-nav-wrap">
      <div className="site-nav">
        <Link
          className="nav-logo"
          to="/"
          aria-label="На главную"
        >
          <img className="brand-logo-img" src={brandLogo} width="146" height="34" alt="Бюро Души" />
        </Link>

        <ul className="nl" aria-label="Навигация">
          <li>
            <NavLink to="/about" className={({ isActive }) => `site-nav-link ${isActive ? 'active' : ''}`}>
              О нас
            </NavLink>
          </li>
          <li>
            <NavLink to="/pricing" className={({ isActive }) => `site-nav-link ${isActive ? 'active' : ''}`}>
              Тарифы
            </NavLink>
          </li>
          {booting ? null : isAuthenticated ? (
            <li>
              <NavLink to="/my-appointments" className={({ isActive }) => `site-nav-link ${isActive ? 'active' : ''}`}>
                Мои записи
              </NavLink>
            </li>
          ) : null}
          <li>
            <NavLink to="/blog" className={({ isActive }) => `site-nav-link ${isActive ? 'active' : ''}`}>
              Блог
            </NavLink>
          </li>
          <li>
            <NavLink to="/forum" className={({ isActive }) => `site-nav-link ${isActive ? 'active' : ''}`}>
              Форум
            </NavLink>
          </li>
        </ul>

        <div className="nr">
          {booting ? null : isAuthenticated ? (
            <Link to="/notifications" className="nav-notif" aria-label="Уведомления">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              {unreadTotal > 0 ? <span className="notif-dot" /> : null}
            </Link>
          ) : null}

          {booting ? null : isAuthenticated ? (
            <button type="button" className="nav-user" onClick={() => navigate('/dashboard')}>
              <span className="nav-avatar">
                <span className="nav-avatar-fallback">{initials}</span>
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={me?.name || me?.email || 'Профиль'}
                    loading="lazy"
                    decoding="async"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                ) : null}
              </span>
              <span className="nav-user-meta">
                <span className="nav-uname">{me?.name || me?.email || 'Профиль'}</span>
                <span className="nav-urole">{roleLabel}</span>
              </span>
            </button>
          ) : (
            <Link to="/login" className="nav-login">Войти</Link>
          )}

          <button
            type="button"
            className={`burger-btn ${menuOpen ? 'open' : ''}`}
            aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      <ul className={`nav-links-m ${menuOpen ? 'open' : ''}`}>
        <li>
          <Link to="/about">О нас</Link>
        </li>
        <li>
          <Link to="/pricing">Тарифы</Link>
        </li>
        {booting ? null : isAuthenticated ? (
          <li>
            <Link to="/my-appointments">Мои записи</Link>
          </li>
        ) : null}
        <li>
          <Link to="/blog">Блог</Link>
        </li>
        <li>
          <Link to="/forum">Форум</Link>
        </li>

        {booting ? null : isAuthenticated ? (
          <li>
            <button type="button" className="nav-links-m__logout" onClick={onLogout}>
              Выйти
            </button>
          </li>
        ) : (
          <li>
            <Link to="/login">Войти</Link>
          </li>
        )}
      </ul>
    </header>
  );
}
